/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HeaderModule
 */

import {
    Component,
    Inject,
    OnDestroy,
} from '@angular/core';

import * as globalL10n from 'global-l10n';
import { StringService } from 'string-service';

import {
    ControllerSiteCollection,
    TControllerSiteCollection,
} from 'collections/controller-site.collection.factory';

import './avi-main-header-controller-site-selector.component.less';

const { ...globalL10nKeys } = globalL10n;

/**
 * @description
 *     Dropdown showing the current controller site, if there are multiple controllers. On change,
 *     opens a new tab to that controller site.
 * @author alextsg
 */
@Component({
    selector: 'avi-main-header-controller-site-selector',
    templateUrl: './avi-main-header-controller-site-selector.component.html',
})
export class AviMainHeaderControllerSiteSelectorComponent implements OnDestroy {
    public controllerSiteCollection: ControllerSiteCollection;

    public selectedControllerSiteRef: string;

    public hasControllerSites = false;

    public globalL10nKeys = globalL10nKeys;

    /**
     * Cache of the current controller site ref. Used to reset the selectedControllerSiteRef
     * ngModel value.
     */
    private currentControllerSiteRef: string;

    constructor(
        private readonly stringService: StringService,
        @Inject(ControllerSiteCollection)
        private readonly ControllerSiteCollectionClass: TControllerSiteCollection,
    ) {
        this.controllerSiteCollection = new ControllerSiteCollectionClass();
    }

    /**
     * @override
     * Load controller site collection to find the current site ref.
     */
    public ngOnInit(): void {
        const controllerSiteCollection = new this.ControllerSiteCollectionClass();

        // Set page size to 1
        controllerSiteCollection.updateViewportSize(1, true, true)
            .then(() => {
                // Make call to make a one site load to get total record count property
                return controllerSiteCollection.load()
                    .then(() => this.onFirstSiteLoad(controllerSiteCollection));
            })
            .finally(() => controllerSiteCollection.destroy());
    }

    /** @override */
    public ngOnDestroy(): void {
        this.controllerSiteCollection.destroy();
    }

    /**
     * Open new tab of new address when controller site changes.
     * Restore ngModel value to current controller site ref after new tab is opened.
     */
    public handleControllerSiteChange(selectedRef: string): void {
        const id = this.stringService.slug(selectedRef);
        const controllerSite = this.controllerSiteCollection.getItemById(id);

        window.open(`https://${controllerSite.getAddress()}`, '_blank');
        this.selectedControllerSiteRef = this.currentControllerSiteRef;
    }

    /**
     * Set hasControllerSites based on presence of any sites and obtain current site's name. In
     * absence of it, use host name to display it.
     */
    private onFirstSiteLoad(controllerSiteCollection: ControllerSiteCollection): void {
        const itemCount = controllerSiteCollection.getTotalNumberOfItems();

        this.hasControllerSites = Boolean(itemCount);

        if (this.hasControllerSites) {
            const controllerSiteItem = controllerSiteCollection.items[0];
            const ref = controllerSiteItem.getRef();
            const currentHost = window.location.host;
            const { address } = controllerSiteItem.getConfig();

            if (address === currentHost) {
                this.setCurrentSiteRef(ref);
            } else { // multiple records
                this.getControllerSiteRefByAddress(currentHost)
                    .then(loadedRef => {
                        if (loadedRef) { // current host exists in list
                            this.setCurrentSiteRef(loadedRef);
                        } else {
                            this.setCurrentSiteRef(currentHost);
                        }
                    });
            }
        }
    }

    /**
     * Set current controller site and selected refs.
     */
    private setCurrentSiteRef(ref: string): void {
        this.selectedControllerSiteRef = ref;
        this.currentControllerSiteRef = ref;
    }

    /**
     * Load ControllerSite collection to get controller site ref by address.
     */
    private getControllerSiteRefByAddress(address: string): Promise<string | void> {
        const collection = new this.ControllerSiteCollectionClass({ limit: 1 });

        collection.setParams({
            address,
            fields: 'url,tenant_ref',
        });

        return collection.load()
            .then(() => {
                if (collection.getTotalNumberOfItems()) {
                    return collection.items[0].getRef();
                }
            })
            .finally(() => collection.destroy());
    }
}
